/**
 * ComplaintCreatePage — `/customer-service/complaints/new`.
 *
 * Operator surface for filing a new service-desk case. Backs onto:
 *   - GET  /v1/service-desk/catalog               (form picker)
 *   - GET  /v1/service-desk/catalog/:formKey      (fieldsSchema for dynamic UI)
 *   - GET  /v1/customers/lookup                   (on-behalf customer picker)
 *   - POST /v1/service-desk/cases                 (the actual submission)
 *
 * The backend supports anonymous walk-in cases (customerId is optional). When
 * a customer is selected we pass `customerId`; otherwise we pass an optional
 * `subjectRef` (NIN / plate / tracking #) that the service hashes.
 *
 * The dynamic field renderer covers the JSON-Schema shapes the catalog
 * actually uses today (string + enum + number + boolean + date/email).
 * Unsupported shapes show a "fill via mobile app" placeholder so the rest of
 * the form keeps working.
 */
import { type FormEvent, type JSX, useEffect, useMemo, useRef, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useMutation, useQuery } from '@tanstack/react-query';

import { ApiError } from '../../lib/api/client';
import { lookupCustomers } from '../../lib/api/customers';
import type { CustomerResponse } from '../../lib/api/types';
import {
  type CasePriority,
  type CatalogFormDetail,
  getCaseCatalogForm,
  listCaseCatalog,
  openCase,
} from '../../lib/api/service-desk';
import { PageHeader } from '../shared/StaffUI';

const PRIORITIES: readonly CasePriority[] = ['LOW', 'MEDIUM', 'HIGH', 'CRITICAL'];

interface SelectedCustomer {
  readonly id: string;
  readonly name: string;
  readonly phone: string;
}

interface SchemaProperty {
  readonly type?: string;
  readonly enum?: readonly string[];
  readonly minLength?: number;
  readonly maxLength?: number;
  readonly minimum?: number;
  readonly maximum?: number;
  readonly format?: string;
  readonly title?: string;
  readonly description?: string;
}

interface FieldsSchema {
  readonly type?: string;
  readonly required?: readonly string[];
  readonly properties?: Record<string, SchemaProperty>;
}

function readFieldsSchema(form: CatalogFormDetail | undefined): FieldsSchema {
  if (!form) return {};
  const raw = form.fieldsSchema as FieldsSchema | undefined;
  return raw ?? {};
}

function fieldLabel(name: string, prop: SchemaProperty): string {
  if (prop.title) return prop.title;
  return name
    .replace(/[_-]+/g, ' ')
    .replace(/\b\w/g, (c) => c.toUpperCase());
}

/** Extract a server-side validation message from an ApiError 422 body. */
function extractServerMessage(err: ApiError): string {
  const body = err.body as
    | { message?: string | string[]; errors?: { path?: string; message?: string }[] }
    | null;
  if (!body) return err.message;
  if (typeof body.message === 'string') return body.message;
  if (Array.isArray(body.message)) return body.message.join('; ');
  if (Array.isArray(body.errors) && body.errors.length > 0) {
    return body.errors
      .map((e) => (e.path ? `${e.path}: ${e.message ?? ''}` : (e.message ?? '')))
      .join('; ');
  }
  return err.message;
}

export function ComplaintCreatePage(): JSX.Element {
  const navigate = useNavigate();

  // --- Step 1: customer mode -----------------------------------------------
  const [mode, setMode] = useState<'CUSTOMER' | 'ANONYMOUS'>('CUSTOMER');
  const [customerQuery, setCustomerQuery] = useState<string>('');
  const [debouncedQuery, setDebouncedQuery] = useState<string>('');
  const [selectedCustomer, setSelectedCustomer] = useState<SelectedCustomer | null>(null);
  const [subjectRef, setSubjectRef] = useState<string>('');

  // Debounce the customer search input (300 ms).
  const debounceTimer = useRef<ReturnType<typeof setTimeout> | null>(null);
  useEffect(() => {
    if (debounceTimer.current !== null) clearTimeout(debounceTimer.current);
    debounceTimer.current = setTimeout(() => {
      setDebouncedQuery(customerQuery.trim());
    }, 300);
    return () => {
      if (debounceTimer.current !== null) clearTimeout(debounceTimer.current);
    };
  }, [customerQuery]);

  const customerLookup = useQuery({
    queryKey: ['cs', 'open-case', 'lookup', debouncedQuery],
    queryFn: () =>
      lookupCustomers({
        // Backend lookup wrapper accepts phone/fullName/email/customerNumber.
        // We pick the most likely field based on input shape.
        ...(/^[+\d]/.test(debouncedQuery)
          ? { phone: debouncedQuery }
          : debouncedQuery.includes('@')
            ? { email: debouncedQuery }
            : { fullName: debouncedQuery }),
        pageSize: 10,
      }),
    enabled: mode === 'CUSTOMER' && debouncedQuery.length >= 2 && selectedCustomer === null,
    retry: false,
    staleTime: 10_000,
  });

  // --- Step 2: catalog form ------------------------------------------------
  const [formKey, setFormKey] = useState<string>('');

  const catalog = useQuery({
    queryKey: ['cs', 'open-case', 'catalog'],
    queryFn: listCaseCatalog,
    retry: false,
    staleTime: 60_000,
  });

  const formDetail = useQuery({
    queryKey: ['cs', 'open-case', 'catalog', formKey],
    queryFn: () => getCaseCatalogForm(formKey),
    enabled: formKey !== '',
    retry: false,
    staleTime: 60_000,
  });

  // --- Step 3: case details ------------------------------------------------
  const [title, setTitle] = useState<string>('');
  const [priority, setPriority] = useState<CasePriority | ''>('');
  const [fieldValues, setFieldValues] = useState<Record<string, string | number | boolean>>({});

  // Reset dynamic field values + priority default whenever the form changes.
  useEffect(() => {
    setFieldValues({});
    if (formDetail.data) {
      const defaultP = formDetail.data.priorityDefault as CasePriority | undefined;
      setPriority(defaultP && PRIORITIES.includes(defaultP) ? defaultP : 'MEDIUM');
    } else {
      setPriority('');
    }
  }, [formDetail.data]);

  const schema = readFieldsSchema(formDetail.data);
  const requiredFields = useMemo<readonly string[]>(
    () => schema.required ?? [],
    [schema.required],
  );

  const [serverError, setServerError] = useState<string | null>(null);
  const [localError, setLocalError] = useState<string | null>(null);

  const submit = useMutation({
    mutationFn: openCase,
    onSuccess: (created) => {
      navigate(`/customer-service/complaints/${encodeURIComponent(created.id)}`, {
        state: { justOpened: created.caseNumber },
      });
    },
    onError: (err) => {
      if (err instanceof ApiError) {
        if (err.status === 403) {
          setServerError('You do not have permission to open cases on behalf of customers.');
          return;
        }
        if (err.status === 422 || err.status === 400) {
          setServerError(extractServerMessage(err));
          return;
        }
        setServerError(extractServerMessage(err));
        return;
      }
      setServerError((err as Error).message);
    },
  });

  function setField(name: string, value: string | number | boolean): void {
    setFieldValues((prev) => ({ ...prev, [name]: value }));
  }

  function buildFormData(): Record<string, unknown> {
    // Drop empty strings for optional fields; required fields are validated
    // before we get here.
    const out: Record<string, unknown> = {};
    for (const [k, v] of Object.entries(fieldValues)) {
      if (typeof v === 'string' && v.trim() === '' && !requiredFields.includes(k)) {
        continue;
      }
      out[k] = v;
    }
    return out;
  }

  function validate(): string | null {
    if (title.trim().length < 2) return 'Title is required (min 2 chars).';
    if (title.trim().length > 200) return 'Title must be 200 chars or fewer.';
    if (formKey === '') return 'Pick a form / category.';
    if (mode === 'CUSTOMER' && selectedCustomer === null) {
      return 'Select a customer, or switch to anonymous walk-in.';
    }
    for (const name of requiredFields) {
      const v = fieldValues[name];
      if (v === undefined || (typeof v === 'string' && v.trim() === '')) {
        return `Required field missing: ${fieldLabel(name, schema.properties?.[name] ?? {})}.`;
      }
    }
    return null;
  }

  function onSubmit(e: FormEvent<HTMLFormElement>): void {
    e.preventDefault();
    setServerError(null);
    const err = validate();
    if (err !== null) {
      setLocalError(err);
      return;
    }
    setLocalError(null);
    submit.mutate({
      formKey,
      title: title.trim(),
      formData: buildFormData(),
      ...(mode === 'CUSTOMER' && selectedCustomer !== null
        ? { customerId: selectedCustomer.id }
        : {}),
      ...(mode === 'ANONYMOUS' && subjectRef.trim() !== ''
        ? { subjectRef: subjectRef.trim() }
        : {}),
      ...(priority !== '' ? { priority } : {}),
    });
  }

  return (
    <div className="space-y-6 max-w-3xl">
      <PageHeader
        title="File a complaint"
        description="Open a new service-desk case on behalf of a customer or as anonymous walk-in."
        actions={
          <Link
            to="/customer-service/complaints"
            className="text-sm font-semibold text-muted hover:underline"
          >
            ← Back to queue
          </Link>
        }
      />

      <form onSubmit={onSubmit} className="space-y-6" data-testid="complaint-create-form">
        {/* Step 1: Customer */}
        <section className="bg-white border border-border rounded-xl p-5 space-y-4">
          <header>
            <h2 className="text-sm font-semibold text-gray-900">Step 1 · Customer</h2>
            <p className="text-xs text-muted">
              File on behalf of an existing customer, or capture an anonymous walk-in case.
            </p>
          </header>

          <div className="flex gap-2" role="radiogroup" aria-label="Filing mode">
            <button
              type="button"
              role="radio"
              aria-checked={mode === 'CUSTOMER'}
              onClick={() => {
                setMode('CUSTOMER');
                setSubjectRef('');
              }}
              className={`flex-1 px-3 py-2 text-sm rounded-lg border ${
                mode === 'CUSTOMER'
                  ? 'border-nipost-green bg-nipost-green-light text-nipost-green font-semibold'
                  : 'border-border bg-white text-gray-700'
              }`}
              data-testid="mode-customer"
            >
              On behalf of a customer
            </button>
            <button
              type="button"
              role="radio"
              aria-checked={mode === 'ANONYMOUS'}
              onClick={() => {
                setMode('ANONYMOUS');
                setSelectedCustomer(null);
                setCustomerQuery('');
              }}
              className={`flex-1 px-3 py-2 text-sm rounded-lg border ${
                mode === 'ANONYMOUS'
                  ? 'border-nipost-green bg-nipost-green-light text-nipost-green font-semibold'
                  : 'border-border bg-white text-gray-700'
              }`}
              data-testid="mode-anonymous"
            >
              Anonymous / walk-in
            </button>
          </div>

          {mode === 'CUSTOMER' ? (
            <div className="space-y-2">
              {selectedCustomer === null ? (
                <>
                  <label className="block">
                    <span className="block text-xs font-medium text-muted mb-1">
                      Search by phone, email, or name
                    </span>
                    <input
                      value={customerQuery}
                      onChange={(e) => setCustomerQuery(e.target.value)}
                      placeholder="+2348012345678 / ada@example.com / Ada"
                      className="w-full px-3 py-2 text-sm border border-border rounded-lg"
                      data-testid="customer-search-input"
                      autoComplete="off"
                    />
                  </label>
                  {debouncedQuery.length >= 2 ? (
                    <div
                      className="border border-border rounded-lg divide-y divide-border max-h-64 overflow-auto"
                      data-testid="customer-search-results"
                    >
                      {customerLookup.isPending ? (
                        <p className="px-3 py-2 text-xs text-muted">Searching…</p>
                      ) : customerLookup.isError ? (
                        <p className="px-3 py-2 text-xs text-red-600">
                          Lookup failed: {(customerLookup.error as Error).message}
                        </p>
                      ) : (customerLookup.data?.items ?? []).length === 0 ? (
                        <p className="px-3 py-2 text-xs text-muted">No customers match.</p>
                      ) : (
                        (customerLookup.data?.items ?? []).slice(0, 10).map((c: CustomerResponse) => (
                          <button
                            type="button"
                            key={c.id}
                            onClick={() =>
                              setSelectedCustomer({
                                id: c.id,
                                name: c.fullName,
                                phone: c.primaryPhone,
                              })
                            }
                            className="block w-full text-left px-3 py-2 text-xs hover:bg-gray-50"
                            data-testid={`customer-result-${c.id}`}
                          >
                            <span className="font-medium text-gray-900">{c.fullName}</span>
                            <span className="ml-2 text-muted">{c.primaryPhone}</span>
                          </button>
                        ))
                      )}
                    </div>
                  ) : (
                    <p className="text-xs text-muted">
                      Type at least 2 characters to search.
                    </p>
                  )}
                </>
              ) : (
                <div
                  className="flex items-center justify-between bg-nipost-green-light border border-nipost-green/40 rounded-lg px-3 py-2"
                  data-testid="customer-selected"
                >
                  <div className="text-xs">
                    <span className="font-semibold text-gray-900">{selectedCustomer.name}</span>
                    <span className="ml-2 text-muted">{selectedCustomer.phone}</span>
                  </div>
                  <button
                    type="button"
                    onClick={() => {
                      setSelectedCustomer(null);
                      setCustomerQuery('');
                    }}
                    className="text-xs font-semibold text-nipost-green hover:underline"
                    data-testid="customer-clear"
                  >
                    Clear
                  </button>
                </div>
              )}
            </div>
          ) : (
            <label className="block">
              <span className="block text-xs font-medium text-muted mb-1">
                Subject reference (NIN, plate, tracking #) — optional
              </span>
              <input
                value={subjectRef}
                onChange={(e) => setSubjectRef(e.target.value)}
                placeholder="e.g. 12345678901 / ABC-123-XY / TRK-001"
                className="w-full px-3 py-2 text-sm border border-border rounded-lg"
                data-testid="subject-ref-input"
                maxLength={80}
              />
              <span className="text-[11px] text-muted mt-1 block">
                Stored hashed — not visible in case data.
              </span>
            </label>
          )}
        </section>

        {/* Step 2: Catalog form */}
        <section className="bg-white border border-border rounded-xl p-5 space-y-4">
          <header>
            <h2 className="text-sm font-semibold text-gray-900">Step 2 · Form</h2>
            <p className="text-xs text-muted">Pick the agency form / complaint category.</p>
          </header>

          <label className="block">
            <span className="block text-xs font-medium text-muted mb-1">
              Form <span className="text-red-600">*</span>
            </span>
            <select
              value={formKey}
              onChange={(e) => setFormKey(e.target.value)}
              className="w-full px-3 py-2 text-sm border border-border rounded-lg bg-white"
              data-testid="form-select"
              aria-label="Form"
            >
              <option value="">Select a form…</option>
              {(catalog.data ?? [])
                .filter((f) => f.active)
                .map((f) => (
                  <option key={f.formKey} value={f.formKey}>
                    {f.formName ?? f.displayName} — {f.agency} · {f.category}
                  </option>
                ))}
            </select>
          </label>

          {catalog.isError ? (
            <p className="text-xs text-red-600" data-testid="catalog-error">
              Could not load catalog: {(catalog.error as Error).message}
            </p>
          ) : null}

          {formDetail.isPending && formKey !== '' ? (
            <p className="text-xs text-muted">Loading form…</p>
          ) : null}
        </section>

        {/* Step 3: Details */}
        <section className="bg-white border border-border rounded-xl p-5 space-y-4">
          <header>
            <h2 className="text-sm font-semibold text-gray-900">Step 3 · Details</h2>
            <p className="text-xs text-muted">Title, priority, and form-specific fields.</p>
          </header>

          <label className="block">
            <span className="block text-xs font-medium text-muted mb-1">
              Title <span className="text-red-600">*</span>
            </span>
            <input
              value={title}
              onChange={(e) => setTitle(e.target.value)}
              placeholder="Short summary of the issue"
              maxLength={200}
              minLength={2}
              className="w-full px-3 py-2 text-sm border border-border rounded-lg"
              data-testid="title-input"
            />
          </label>

          <label className="block">
            <span className="block text-xs font-medium text-muted mb-1">Priority</span>
            <select
              value={priority}
              onChange={(e) => setPriority(e.target.value as CasePriority)}
              className="w-full px-3 py-2 text-sm border border-border rounded-lg bg-white"
              data-testid="priority-select"
              aria-label="Priority"
            >
              {PRIORITIES.map((p) => (
                <option key={p} value={p}>
                  {p}
                </option>
              ))}
            </select>
          </label>

          {/* Dynamic field renderer */}
          {formDetail.data && schema.properties ? (
            <div
              className="space-y-3 pt-2 border-t border-border"
              data-testid="dynamic-fields"
            >
              {Object.entries(schema.properties).map(([name, propRaw]) => {
                const prop: SchemaProperty = propRaw;
                const required = requiredFields.includes(name);
                return (
                  <DynamicField
                    key={name}
                    name={name}
                    prop={prop}
                    required={required}
                    value={fieldValues[name]}
                    onChange={(v) => setField(name, v)}
                  />
                );
              })}
            </div>
          ) : null}
        </section>

        {/* Errors + submit */}
        {localError ? (
          <div
            className="rounded-lg border border-red-200 bg-red-50 px-4 py-3 text-sm text-red-800"
            data-testid="local-error"
          >
            {localError}
          </div>
        ) : null}
        {serverError ? (
          <div
            className="rounded-lg border border-red-200 bg-red-50 px-4 py-3 text-sm text-red-800"
            data-testid="server-error"
          >
            {serverError}
          </div>
        ) : null}

        <div className="flex justify-end gap-2">
          <Link
            to="/customer-service/complaints"
            className="px-4 py-2 text-sm font-semibold text-muted hover:underline"
          >
            Cancel
          </Link>
          <button
            type="submit"
            disabled={submit.isPending}
            className="px-4 py-2 bg-nipost-green text-white text-sm font-semibold rounded-lg hover:bg-nipost-green-dark disabled:opacity-50"
            data-testid="submit-button"
          >
            {submit.isPending ? 'Opening case…' : 'Open case'}
          </button>
        </div>
      </form>
    </div>
  );
}

// ---------------------------------------------------------------------------
// Dynamic field — minimal JSON-Schema renderer.
// ---------------------------------------------------------------------------

interface DynamicFieldProps {
  readonly name: string;
  readonly prop: SchemaProperty;
  readonly required: boolean;
  readonly value: string | number | boolean | undefined;
  readonly onChange: (v: string | number | boolean) => void;
}

function DynamicField({ name, prop, required, value, onChange }: DynamicFieldProps): JSX.Element {
  const label = (
    <span className="block text-xs font-medium text-muted mb-1">
      {fieldLabel(name, prop)}
      {required ? <span className="text-red-600"> *</span> : null}
    </span>
  );

  // String + enum -> select.
  if (prop.type === 'string' && Array.isArray(prop.enum) && prop.enum.length > 0) {
    return (
      <label className="block" data-testid={`field-${name}`}>
        {label}
        <select
          value={typeof value === 'string' ? value : ''}
          onChange={(e) => onChange(e.target.value)}
          className="w-full px-3 py-2 text-sm border border-border rounded-lg bg-white"
          aria-label={fieldLabel(name, prop)}
        >
          <option value="">Select…</option>
          {prop.enum.map((opt) => (
            <option key={opt} value={opt}>
              {opt}
            </option>
          ))}
        </select>
      </label>
    );
  }

  // String -> input (with date / email format hints).
  if (prop.type === 'string') {
    const inputType =
      prop.format === 'date'
        ? 'date'
        : prop.format === 'date-time'
          ? 'datetime-local'
          : prop.format === 'email'
            ? 'email'
            : 'text';
    return (
      <label className="block" data-testid={`field-${name}`}>
        {label}
        <input
          type={inputType}
          value={typeof value === 'string' ? value : ''}
          onChange={(e) => onChange(e.target.value)}
          minLength={prop.minLength}
          maxLength={prop.maxLength}
          className="w-full px-3 py-2 text-sm border border-border rounded-lg"
          aria-label={fieldLabel(name, prop)}
        />
        {prop.description ? (
          <span className="text-[11px] text-muted mt-1 block">{prop.description}</span>
        ) : null}
      </label>
    );
  }

  // Number / integer.
  if (prop.type === 'number' || prop.type === 'integer') {
    return (
      <label className="block" data-testid={`field-${name}`}>
        {label}
        <input
          type="number"
          value={typeof value === 'number' ? value : ''}
          onChange={(e) => {
            const n = e.target.value === '' ? '' : Number(e.target.value);
            if (n === '') return;
            onChange(n);
          }}
          min={prop.minimum}
          max={prop.maximum}
          step={prop.type === 'integer' ? 1 : 'any'}
          className="w-full px-3 py-2 text-sm border border-border rounded-lg"
          aria-label={fieldLabel(name, prop)}
        />
      </label>
    );
  }

  // Boolean -> checkbox.
  if (prop.type === 'boolean') {
    return (
      <label className="flex items-center gap-2" data-testid={`field-${name}`}>
        <input
          type="checkbox"
          checked={value === true}
          onChange={(e) => onChange(e.target.checked)}
          className="h-4 w-4 rounded border-border"
          aria-label={fieldLabel(name, prop)}
        />
        <span className="text-xs text-gray-700">
          {fieldLabel(name, prop)}
          {required ? <span className="text-red-600"> *</span> : null}
        </span>
      </label>
    );
  }

  // Unsupported types — keep the form usable.
  return (
    <div
      className="rounded-lg border border-amber-200 bg-amber-50 px-3 py-2 text-xs text-amber-900"
      data-testid={`field-unsupported-${name}`}
    >
      <p className="font-semibold">
        {fieldLabel(name, prop)}
        {required ? <span className="text-red-600"> *</span> : null}
      </p>
      <p>Unsupported field type — fill via mobile app.</p>
    </div>
  );
}
